import React, { useState, useEffect } from 'react';

interface Dish {
  id: string;
  name: string;
  description: string;
  imagePath?: string;
  quantityLimit?: number;
  noteOptions?: string[];
  isActive: boolean;
  createdAt: string;
}

const DishManager: React.FC = () => {
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingDish, setEditingDish] = useState<Dish | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    quantityLimit: '',
    image: null as File | null,
    noteOptions: [] as string[]
  });
  const [newNoteOption, setNewNoteOption] = useState('');

  useEffect(() => {
    loadDishes();
  }, []);

  const loadDishes = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/v1/dishes');
      const data = await response.json();
      if (data.success) {
        setDishes(data.dishes);
      }
    } catch (error) {
      console.error('Failed to load dishes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const payload = {
        name: formData.name,
        description: formData.description || undefined,
        quantityLimit: formData.quantityLimit ? parseInt(formData.quantityLimit) : undefined,
        noteOptions: formData.noteOptions.length > 0 ? formData.noteOptions : undefined
      };

      const url = editingDish
        ? `/api/v1/dishes/${editingDish.id}`
        : '/api/v1/dishes';

      const response = await fetch(url, {
        method: editingDish ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        await loadDishes();
        setFormData({ name: '', description: '', quantityLimit: '', image: null, noteOptions: [] });
        setNewNoteOption('');
        setShowCreateForm(false);
        setEditingDish(null);
      } else {
        alert(data.error || 'Failed to save dish');
      }
    } catch (error) {
      console.error('Failed to save dish:', error);
      alert('Failed to save dish');
    }
  };

  const handleEdit = (dish: Dish) => {
    setEditingDish(dish);
    setFormData({
      name: dish.name,
      description: dish.description,
      quantityLimit: dish.quantityLimit?.toString() || '',
      image: null,
      noteOptions: dish.noteOptions || []
    });
    setNewNoteOption('');
    setShowCreateForm(true);
  };

  const handleDelete = async (dish: Dish) => {
    if (window.confirm(`האם למחוק את המנה "${dish.name}"?`)) {
      try {
        const response = await fetch(`/api/v1/dishes/${dish.id}`, {
          method: 'DELETE'
        });
        const data = await response.json();
        if (data.success) {
          await loadDishes();
        } else {
          alert(data.error || 'Failed to delete dish');
        }
      } catch (error) {
        console.error('Failed to delete dish:', error);
        alert('Failed to delete dish');
      }
    }
  };

  // Render function (not component) to prevent focus loss on parent re-render
  const renderDishForm = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg mx-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {editingDish ? 'ערוך מנה' : 'הוסף מנה חדשה'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              שם המנה *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              maxLength={50}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="שם המנה"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {formData.name.length}/50 תווים
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              תיאור
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="תיאור המנה"
            />
          </div>

          {/* Quantity Limit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              הגבלת כמות (אופציונלי)
            </label>
            <input
              type="number"
              value={formData.quantityLimit}
              onChange={(e) => setFormData({ ...formData, quantityLimit: e.target.value })}
              min="1"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="מספר מנות מקסימלי"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              השאר ריק לכמות בלתי מוגבלת
            </p>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              תמונה
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setFormData({ ...formData, image: e.target.files?.[0] || null })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              התמונה תמוטב אוטומטית לפורמט WebP
            </p>
          </div>

          {/* Note Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              אפשרויות הערה (אופציונלי)
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              הוסף אפשרויות כמו "well done", "medium", "rare" לבחירת המלצר
            </p>
            {/* Existing options */}
            {formData.noteOptions.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.noteOptions.map((option, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-full text-sm"
                  >
                    {option}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          noteOptions: formData.noteOptions.filter((_, i) => i !== index)
                        });
                      }}
                      className="text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            {/* Add new option */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newNoteOption}
                onChange={(e) => setNewNoteOption(e.target.value)}
                placeholder="הוסף אפשרות..."
                maxLength={50}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (newNoteOption.trim() && !formData.noteOptions.includes(newNoteOption.trim())) {
                      setFormData({
                        ...formData,
                        noteOptions: [...formData.noteOptions, newNoteOption.trim()]
                      });
                      setNewNoteOption('');
                    }
                  }
                }}
              />
              <button
                type="button"
                onClick={() => {
                  if (newNoteOption.trim() && !formData.noteOptions.includes(newNoteOption.trim())) {
                    setFormData({
                      ...formData,
                      noteOptions: [...formData.noteOptions, newNoteOption.trim()]
                    });
                    setNewNoteOption('');
                  }
                }}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                הוסף
              </button>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              {editingDish ? 'עדכן מנה' : 'הוסף מנה'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCreateForm(false);
                setEditingDish(null);
                setFormData({ name: '', description: '', quantityLimit: '', image: null, noteOptions: [] });
                setNewNoteOption('');
              }}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              ביטול
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">ניהול מנות</h2>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            נהל את מאגר המנות לאירועים עתידיים
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          הוסף מנה חדשה
        </button>
      </div>

      {/* Dishes Grid */}
      {dishes.length === 0 ? (
        <div className="text-center py-12">
          <div className="bg-gray-100 dark:bg-gray-800 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            אין מנות במאגר
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            התחל בהוספת מנות למאגר כדי להשתמש בהן באירועים
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            הוסף מנה ראשונה
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dishes.map((dish) => (
            <div key={dish.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
              {/* Dish Image */}
              <div className="h-48 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                {dish.imagePath ? (
                  <img 
                    src={dish.imagePath} 
                    alt={dish.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
              </div>

              {/* Dish Info */}
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  {dish.name}
                </h3>
                {dish.description && (
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
                    {dish.description}
                  </p>
                )}
                {dish.quantityLimit && (
                  <p className="text-blue-600 dark:text-blue-400 text-sm mb-2">
                    מוגבל ל-{dish.quantityLimit} מנות
                  </p>
                )}
                {dish.noteOptions && dish.noteOptions.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {dish.noteOptions.map((option, index) => (
                      <span
                        key={index}
                        className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-0.5 rounded text-xs"
                      >
                        {option}
                      </span>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-3">
                  <button
                    onClick={() => handleEdit(dish)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-3 rounded-lg transition-colors"
                  >
                    ערוך
                  </button>
                  <button
                    onClick={() => handleDelete(dish)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-3 rounded-lg transition-colors"
                  >
                    מחק
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {showCreateForm && renderDishForm()}
    </div>
  );
};

export default DishManager;
